import { defineStore } from 'pinia'
import state from './state'
import { MainToWorkerMessageType, WorkerToMainMessageType } from '@/enum/SignalRWorkerMessageType'
import SignalRGroupType from '@/enum/SignalRGroupType'
import useGlobalStore from '../global'
import useSpreadingJobsStore from '../spreadingJobs'
import useDigestateJobsStore from '../digestateJobs'
import useHaulageJobsStore from '../haulageJobs'
import type { IDigestateJob, IHaulageJob, ISpreadingJob } from '@/interfaces'
import NotificationType, { NotificationDuration } from '@/enum/NotificationType'
import usePaginationStore from '../pagination'
import { NOTIFICATIONS_LIST_STORE } from '@/utils/constants'
import useActivityStore from '../activities'

const useRealtimeStore = defineStore('realtimeStore', {
  state: () => state,
  actions: {
    async subscribeToJobActivityUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Subscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobActivity]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.Subscribed:
            break
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          default:
            break
        }
      }
    },

    async subscribeToHaulageJobUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Subscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobUpdateChannel, SignalRGroupType.HaulageJobUpdate]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.Subscribed:
            break
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          case WorkerToMainMessageType.UpdateHaulageJob:
            this.handleHaulageJobUpdate(rest.data, navigator)
            break
          default:
            break
        }
      }
    },

    async subscribeToDigestateJobUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Subscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobUpdateChannel, SignalRGroupType.DigestateJobUpdate]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.Subscribed:
            break
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          case WorkerToMainMessageType.UpdateDigestateJob:
            this.handleDigestateJobUpdate(rest.data, navigator)
            break
          default:
            break
        }
      }
    },

    async subscribeToSpreadingJobUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Subscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobUpdateChannel, SignalRGroupType.SpreadingJobUpdate]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.Subscribed:
            break
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          case WorkerToMainMessageType.UpdateSpreadingJob:
            this.handleSpreadingJobUpdate(rest.data, navigator)
            break
          default:
            break
        }
      }
    },

    async unsubscribeToHaulageJobUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return

      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Unsubscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobUpdateChannel, SignalRGroupType.HaulageJobUpdate]
      })
    },

    async unsubscribeToDigestateJobUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return

      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Unsubscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobUpdateChannel, SignalRGroupType.DigestateJobUpdate]
      })
    },

    async subscribeToHaulageJobDetailsUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      const jobId = navigator.getCurrentRoute()?.params.id
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.SubscribeGroupById,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.HaulageJobUpdate, jobId]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          case WorkerToMainMessageType.UpdateHaulageJob:
            this.handleHaulageJobUpdate(rest.data, navigator)
            break
          default:
            break
        }
      }
    },

    async unsubscribeToJobDetailsUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      const jobId = navigator.getCurrentRoute()?.params.id

      if (jobId) {
        signalRWorker.postMessage({
          name: MainToWorkerMessageType.UnsubscribeGroup,
          route: navigator?.getCurrentRoute().name,
          channels: [jobId],
          groupId: jobId
        })
      }
    },

    async subscribeToDigestateJobDetailsUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      const jobId = navigator.getCurrentRoute()?.params.id
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.SubscribeGroupById,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.DigestateJobUpdate, jobId]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          case WorkerToMainMessageType.UpdateDigestateJob:
            this.handleDigestateJobUpdate(rest.data, navigator)
            break
          default:
            break
        }
      }
    },

    async subscribeToSpreadingJobDetailsUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      const jobId = navigator.getCurrentRoute()?.params.id
      signalRWorker.postMessage({
        name: MainToWorkerMessageType.SubscribeGroupById,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.SpreadingJobUpdate, jobId]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.UpdateJobActivity:
            this.handleJobUpdateRequest(name, rest.data)
            break
          case WorkerToMainMessageType.UpdateSpreadingJob:
            this.handleSpreadingJobUpdate(rest.data, navigator)
            break
          default:
            break
        }
      }
    },

    async unsubscribeToSpreadingJobUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return

      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Unsubscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [SignalRGroupType.JobUpdateChannel, SignalRGroupType.SpreadingJobUpdate]
      })
    },

    async handleJobUpdateRequest(updateType: any, data: Record<string, any>) {
      const jobStores: Array<string> = ['spreadingJob', 'digestateJob', 'haulageJob']
      for (const jobKey of jobStores) {
        if (data[jobKey]) {
          const globalStore = useGlobalStore()
          const activityStore = useActivityStore()
          const jobUpdate = data[jobKey]
          const isJobActivityUpdate = updateType === WorkerToMainMessageType.UpdateJobActivity

          if (isJobActivityUpdate) {
            if (globalStore.showNotifications) {
              const listStore = usePaginationStore(NOTIFICATIONS_LIST_STORE)
              listStore.resetPaginationState()
              activityStore.getNotificationsActivities(NOTIFICATIONS_LIST_STORE)
              return
            }
            globalStore.hasNewNotification = true
            const isNewJob = jobUpdate?.createdAt === jobUpdate?.updatedAt
            const isJobAssignedToLoggedInUser =
              localStorage.getItem('userId') == jobUpdate?.assignedEmployeeId
            const isJobCreatedByLoggedInUser =
              localStorage.getItem('userId') == jobUpdate?.updaterId

            if (isNewJob && isJobAssignedToLoggedInUser && !isJobCreatedByLoggedInUser) {
              globalStore.addToastNotification({
                id: Symbol('NEW_JOB_ASSIGNED'),
                message: data.description,
                type: NotificationType.Success,
                timeout: NotificationDuration.Medium,
                callback: () => {
                  globalStore.toggleNotificationsFlyout()
                  globalStore.removeAllNotifications()
                  globalStore.hasNewNotification = false
                }
              })
            }
          }
          break
        }
      }
    },

    async handleSpreadingJobUpdate(data: ISpreadingJob, navigator: any) {
      const jobsStore = useSpreadingJobsStore()
      jobsStore.handleRealtimeJobUpdateOnList(data)
      jobsStore.handleRealtimeJobUpdateOnDetails(data, navigator)
    },

    async handleHaulageJobUpdate(data: IHaulageJob, navigator: any) {
      const jobsStore = useHaulageJobsStore()
      jobsStore.handleRealtimeJobUpdateOnList(data)
      jobsStore.handleRealtimeJobUpdateOnDetails(data, navigator)
    },

    async handleDigestateJobUpdate(data: IDigestateJob, navigator: any) {
      const jobsStore = useDigestateJobsStore()
      jobsStore.handleRealtimeJobUpdateOnList(data)
      jobsStore.handleRealtimeJobUpdateOnDetails(data, navigator)
    }
  }
})
export default useRealtimeStore
